import React from "react";
import { Link } from "react-router-dom";
import { Users, Building2, Shield, FileText, Briefcase, ClipboardList, AlertOctagon, CheckCircle2, ArrowRight } from "lucide-react";
import { DashboardShell } from "../../components/shared/DashboardShell";
import { StatCard } from "../../components/shared/StatCard";
import { StatusBadge } from "../../components/shared/StatusBadge";
import { EmptyState, LoadingState, ErrorState } from "../../components/shared/EmptyState";
import { adminNav } from "../../components/shared/navConfig";
import { useApi, inr } from "../../lib/useApi";

const QUEUES = [
  ["pending_kyc", "KYC to review", "/admin/kyc", Shield],
  ["pending_gst", "GST to review", "/admin/gst", FileText],
  ["pending_tasks", "Tasks to approve", "/admin/tasks", Briefcase],
  ["open_disputes", "Open disputes", "/admin/disputes", AlertOctagon],
];

export default function AdminDashboard() {
  const { data: d, loading, error, reload } = useApi("/admin/overview");
  return (
    <DashboardShell nav={adminNav} title="Operations overview" subtitle="Live platform numbers and pending queues">
      {loading ? <LoadingState/> : error ? <ErrorState message={error} onRetry={reload}/> : (
        <>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 mb-6">
            <StatCard testId="admin-stat-students" label="Students" value={d.students} icon={Users} accent/>
            <StatCard testId="admin-stat-verified-students" label="KYC verified" value={d.verified_students} icon={CheckCircle2}/>
            <StatCard testId="admin-stat-companies" label="Companies" value={d.companies} icon={Building2}/>
            <StatCard testId="admin-stat-verified-companies" label="GST verified" value={d.verified_companies} icon={CheckCircle2}/>
            <StatCard testId="admin-stat-live-tasks" label="Live tasks" value={d.live_tasks} icon={Briefcase} accent/>
            <StatCard testId="admin-stat-applications" label="Applications" value={d.applications} icon={ClipboardList}/>
          </div>
          <div className="grid lg:grid-cols-3 gap-5">
            <div className="card-surface p-4 sm:p-5">
              <div className="eyebrow mb-3">Needs your attention</div>
              {QUEUES.map(([k, l, to, Icon]) => (
                <Link key={k} to={to} data-testid={`admin-queue-${k}`} className="flex items-center gap-3 py-3 border-b last:border-b-0 hover:opacity-80 transition-opacity" style={{ borderColor: "var(--border)" }}>
                  <Icon size={15} className="text-blue-500"/><span className="flex-1 text-sm" style={{ color: "var(--text-muted)" }}>{l}</span>
                  <span className="font-mono text-sm" style={{ color: d[k] ? "#F59E0B" : "var(--text-dim)" }}>{d[k]}</span><ArrowRight size={12} style={{ color: "var(--text-dim)" }}/>
                </Link>
              ))}
            </div>
            <div className="lg:col-span-2 card-surface p-4 sm:p-5">
              <div className="flex items-center justify-between mb-3">
                <div><div className="eyebrow">Recent tasks</div><h3 className="font-display text-lg mt-1" style={{ color: "var(--text)" }}>Latest submissions</h3></div>
                <Link to="/admin/find-tasks" data-testid="admin-view-tasks-link" className="text-xs text-blue-500 inline-flex items-center gap-1">All tasks <ArrowRight size={12}/></Link>
              </div>
              {!d.recent_tasks.length ? <EmptyState icon={Briefcase} title="No tasks yet" desc="Tasks from verified companies will appear here." testId="admin-tasks-empty"/> : (
                <table className="table-stack w-full text-sm">
                  <thead><tr className="text-[10px] uppercase tracking-widest text-left" style={{ color: "var(--text-dim)" }}><th className="pb-3 font-normal">Task</th><th className="pb-3 font-normal">Location</th><th className="pb-3 font-normal">Budget</th><th className="pb-3 font-normal text-right">Status</th></tr></thead>
                  <tbody>{d.recent_tasks.map(t => (
                    <tr key={t.task_id} data-testid={`admin-task-row-${t.task_id}`} className="border-t" style={{ borderColor: "var(--border)" }}>
                      <td className="py-3"><div style={{ color: "var(--text)" }}>{t.title}</div><div className="text-[11px]" style={{ color: "var(--text-dim)" }}>{t.company_name} · {t.task_id}</div></td>
                      <td data-label="Location" className="text-xs" style={{ color: "var(--text-muted)" }}><span>{t.city || "—"}</span></td>
                      <td data-label="Budget" className="text-xs font-mono" style={{ color: "var(--text)" }}><span>{inr(t.budget)}</span></td>
                      <td data-label="Status" className="text-right"><StatusBadge status={t.status}/></td>
                    </tr>
                  ))}</tbody>
                </table>
              )}
            </div>
          </div>
        </>
      )}
    </DashboardShell>
  );
}
